import type { CellViewWire } from '@badger-sentinel/protocol';

import { compareAliases } from './alias.js';
import {
  ACCESS_MARK,
  ACCESS_PIPS,
  ACCESS_SHORT,
  FOOTING,
  accessRung as cyberAccessRung,
  cyberModel,
  footingFor,
  type AccessMark,
  type CyberAccess,
  type CyberGateway,
  type CyberHardening,
  type CyberRouter,
  type Footing,
} from './cyber.js';
import { cyberIconHref } from './cyber-icons.js';
import { packCoverage, type MapPack, type MapPackRegion } from './map-pack.js';
import { rangesFrom, weatherFor } from './selection.js';
import { iconFor, sidcFor, type SidcIcon } from './sidc.js';
import type { ContactLocationWire, FriendlyAssetViewWire } from './view-types.js';

/**
 * `scenario.js`, rebuilt from `CellViewWire` — Phase 0's finding 1, discharged.
 *
 * > **The vendored maps import the mock data directly.** … Vendoring them
 * > verbatim (M5-D7) therefore means M5-D8's `model/` layer must expose a
 * > `scenario.js`-shaped facade built from `CellViewWire`, not just view models
 * > for React.
 *
 * `vendor/maps/region-map.js` opens `boot()` with
 * `const mod = await import('./scenario.js?v=3'); this.S = mod;` and then reads
 * exactly eight members off it. This module is those eight, and nothing else —
 * the renderer's vocabulary on the outside, the wire on the inside:
 *
 * | Member | Read by | Built from |
 * | --- | --- | --- |
 * | `REGIONS` | `buildRings`, `resize`, `geometry`, `slotsFor`, `drawRegions`, `layout`, `regionNames` | `map.regions` joined to the map pack |
 * | `UNITS` | `rosterOf` | `friendly[]` + `contacts[]` |
 * | `unitById` | `select`, `focusUnit`, `fitSide` | the same list |
 * | `visible` | `unitsOf` | see below — always true |
 * | `isCyberNode` | `rosterOf`, `rangeOverlay` | `segment === 'CYBER'` |
 * | `reachBands` | `rangeOverlay` | `map.ranges`, and only `map.ranges` |
 * | `weatherOf` | `drawRegions`, `regionNames` | `theatre.{weather, forecast}` |
 * | `WX_GLYPH` | `regionNames` | a constant table |
 * | `slotsIn` | `orbital-map.js`'s `slotCount` | `map.bandSlots` |
 *
 * `cyber-map.js` then reads **eleven more**, and they are the phase's whole
 * shape: `ROUTERS`, `NETWORKS`, `hostsOf`, `routerIdOf`, `isCyberTeam`,
 * `accessFor`, `access`, `hardenOf`, `accessRung`, `footingFor` and `iconHref`,
 * plus the three constant tables `ACCESS_MARK`, `ACCESS_SHORT` and `FOOTING`.
 * All fourteen are built by `model/cyber.ts`, which is where the argument for
 * each of them lives; this module only adopts them, because the renderer imports
 * one facade and there is one.
 *
 * The prototype's other forty exports — `EDGES`, `rangeFrom`, `pairOptions`,
 * `successPct`, the cyber tables — are read by the *shell* in the prototype, and
 * our shell has its own answers for every one of them (`model/ladder.ts`,
 * `model/missions.ts`, `model/selection.ts`). None of them is rebuilt here.
 *
 * ## Two things this facade will not do
 *
 * **It computes no distance.** `reachBands` is a lookup in `map.ranges` — the
 * declared pair table, which obeys nothing (Q3) — plus the origin at zero. The
 * prototype's Dijkstra is not ported, the pack's `site` coordinates are never
 * subtracted from each other, and no pixel on the canvas can be read as a range
 * (must-not #1). A range table that contradicts the geography still draws the
 * table's numbers.
 *
 * **It cannot tell a phantom from a contact.** Every field a unit record carries
 * comes off `ContactView`, which a phantom carries identically, and the list is
 * ordered by `compareAliases` so that array position on the wire is not a
 * channel (must-not #5, `model/alias.ts`).
 */

// ---------------------------------------------------------------------------
// The renderer's vocabulary
// ---------------------------------------------------------------------------

export interface ScenarioRegion {
  readonly id: string;
  /** Drawn on the canvas. The wire's name, never the pack's source. */
  readonly name: string;
  readonly sources: MapPackRegion['sources'];
}

/**
 * One unit, in `region-map.js`'s own shape.
 *
 * Only the fields the renderer reads, and only the ones the wire can answer. It
 * has no `reach` (no asset carries one), no `custody` (nothing on `ContactView`
 * says how old a track is in the prototype's `T-n` vocabulary) and no `orbitalReach`
 * — three bindings deleted rather than defaulted (M5-D10).
 */
export interface ScenarioUnit {
  /** An asset id, or a contact's per-observer alias. Drawn as the marker label. */
  readonly id: string;
  /** Viewer-relative, as everywhere else in the console: own is BLU, a contact is OPF. */
  readonly side: 'BLU' | 'OPF';
  /** Present for anything the theatre map can place: `GROUND` and `REGION`. */
  readonly region?: string;
  /**
   * Present for anything the orbital board can place — and **only** then.
   *
   * The renderer keys a marker on `regime + ':' + slot`, so a unit with a regime
   * and no slot is created at `NaN` and drawn nowhere: present in the DOM,
   * reported by nothing. So the two fields travel together, and the rule that
   * sets them is one sentence: *a marker on a ring means the wire named a slot.*
   * A `BAND` contact, an asset in `TRANSIT` and a contact at `UNKNOWN` carry
   * neither, and `model/orbital.ts` is where they are drawn instead.
   */
  readonly regime?: string;
  /** The wire's slot, in the band's own numbering — 1…24, or 1…4 in HEO. */
  readonly slot?: number;
  readonly icon: SidcIcon;
  /** From `model/sidc.ts`, so the renderer's own `SIDC_MAP` is never consulted. */
  readonly sidc: string;
  readonly name: string;
  readonly desc?: string;
  readonly status: string;
  /** Contacts only: `FOUND` dashes the frame, `FIXED` doubles it, `TRACKED` is solid. */
  readonly track?: string;
  readonly cyber?: boolean;
}

export interface ReachBands {
  /** Region id → declared range from this unit's own region. Origin is `0`. */
  readonly cost: Readonly<Record<string, number>>;
  readonly count: number;
  readonly total: number;
}

export interface ScenarioModel {
  readonly REGIONS: readonly ScenarioRegion[];
  readonly UNITS: readonly ScenarioUnit[];
  readonly WX_GLYPH: Readonly<Record<string, string>>;
  unitById(id: string): ScenarioUnit | undefined;
  visible(unit: ScenarioUnit): boolean;
  isCyberNode(unit: ScenarioUnit): boolean;
  reachBands(unit: ScenarioUnit): ReachBands;
  weatherOf(region: string): readonly [string | null, string | null];
  /** Slots in one band. 24 everywhere but HEO, which has 4 (`map.bandSlots`). */
  slotsIn(band: string): number;

  // --- the cyber board's half (`model/cyber.ts`) ---------------------------
  readonly ROUTERS: readonly CyberRouter[];
  readonly NETWORKS: readonly CyberGateway[];
  readonly ACCESS_MARK: Readonly<Record<string, AccessMark | null>>;
  readonly ACCESS_SHORT: readonly string[];
  readonly ACCESS_PIPS: number;
  readonly FOOTING: Readonly<Record<string, Footing>>;
  hostsOf(routerId: string): readonly ScenarioUnit[];
  routerIdOf(unitId: string): string;
  /**
   * A cyber team is an **actor**, not a host — an operator that stages in transit
   * and reaches into either network, and never appears in a host grid.
   *
   * Not the same question as `isCyberNode`, even though on this wire the two
   * happen to coincide. `isCyberNode` asks *"should the theatre map skip this"*
   * and this asks *"does this dock under a router rather than hang off one"*. One
   * predicate answering both would be the first thing to go wrong the day a
   * scenario puts a non-team asset in the CYBER segment.
   */
  isCyberTeam(unit: ScenarioUnit): boolean;
  accessFor(unit: { readonly id: string }): CyberAccess;
  access(id: string): CyberAccess;
  hardenOf(id: string): CyberHardening;
  accessRung(access: string): number;
  footingFor(hardening: string): Footing;
  /** The card artwork's url — vendored edit 1's other half. */
  iconHref(cls: string): string;
  /** Our network id for a router key. The `bs-region` translation. */
  networkOf(routerId: string): string | null;
}

export type ScenarioModelResult =
  | { readonly ok: true; readonly model: ScenarioModel }
  | { readonly ok: false; readonly missing: readonly string[] };

/**
 * The mark drawn beside a region's name while the weather layer is on.
 *
 * **It is the hatch it labels.** `region-map.js` fills a restrictive zone with a
 * single-diagonal pattern and a prohibitive one with a cross-hatch, so the mark
 * beside the name is `/` and `×` — one vocabulary on the canvas, readable
 * without matching hatch densities across the map, and legible with the colour
 * channel gone.
 *
 * It is *not* the design's Material Symbols ligature (`sunny`, `thunderstorm`).
 * Phase 0's finding 4 dropped that font — 5.3 MB for six glyphs — so the
 * ligature would render as the literal word `thunderstorm` in the fallback face.
 * The rail's `WeatherGlyph` carries the icon vocabulary from inlined paths;
 * a `<text>` node cannot.
 *
 * `null` and `undefined` are **declared keys**, because the renderer indexes this
 * with whatever `weatherOf` returned and `textContent = undefined` renders the
 * word "undefined" on the map. A region the wire has no weather for reads `?`,
 * which is `WeatherGlyph`'s unknown state and never CLEAR (must-not: an absent
 * value is never drawn as a default).
 */
export const WX_GLYPH: Readonly<Record<string, string>> = Object.freeze({
  CLEAR: '',
  RESTRICTIVE: '/',
  PROHIBITIVE: '×',
  null: '?',
  undefined: '?',
});

/**
 * Can the theatre map place this subject at all?
 *
 * `GROUND` for an asset and `REGION` for a contact, and nothing else: an asset in
 * orbit is the SDA board's (Phase 6), a cyber team hangs off a router (Phase 7),
 * and `OFFMAP` is nowhere by definition. Asked in two places — the `F` key and
 * the unit menu's FOCUS ON MAP — so it is one function rather than two
 * predicates that will disagree the first time a location kind is added.
 */
export function canPlaceOnTheatre(view: CellViewWire, id: string): boolean {
  const asset = view.friendly.find((candidate) => candidate.id === id);
  if (asset !== undefined) return asset.location.kind === 'GROUND';
  const contact = view.contacts.find((candidate) => candidate.id === id);
  return contact !== undefined && contact.location.kind === 'REGION';
}

/**
 * Can the orbital board place this subject on a ring?
 *
 * `ORBIT` for an asset and `SLOT` for a contact, and nothing else — the same
 * one-sentence rule the facade places markers by. An asset in `TRANSIT` and a
 * `BAND` contact are on the board but not on a ring, so `F` and FOCUS ON MAP
 * stay unbound for them: a camera move onto a marker that is not there teaches
 * the operator the console is broken.
 */
export function canPlaceOnOrbit(view: CellViewWire, id: string): boolean {
  const asset = view.friendly.find((candidate) => candidate.id === id);
  if (asset !== undefined) return asset.location.kind === 'ORBIT';
  const contact = view.contacts.find((candidate) => candidate.id === id);
  return contact !== undefined && contact.location.kind === 'SLOT';
}

/**
 * Can the cyber board place this subject?
 *
 * The third of these, and the widest — because the cyber board is the one canvas
 * with a place for **everything the seat owns**. Every asset carries a `network`
 * (M5 F4), so every asset hangs off an enclave whatever its physical location
 * is; a cyber team docks under its router rather than hanging off it, and it is
 * still on the board.
 *
 * That is what makes `OFFMAP` interesting rather than a dead end. Phase 5 and
 * Phase 6 both refused a cyber team — correctly, because a team is nowhere on
 * the ground and nowhere in orbit — and `F` was therefore unbound for the CYBER
 * seat's only orderable asset. This is the board that can place it.
 *
 * The enemy half is `cyber.contacts[]` and not `contacts[]`: a machine this seat
 * has found but has no cyber access to is not on this canvas, and centring the
 * camera on a card that is not there teaches the operator the console is broken.
 */
export function canPlaceOnCyber(view: CellViewWire, id: string): boolean {
  if (view.friendly.some((asset) => asset.id === id)) return true;
  return view.cyber.contacts.some((contact) => contact.alias === id);
}

// ---------------------------------------------------------------------------
// Building it
// ---------------------------------------------------------------------------

function assetUnit(asset: FriendlyAssetViewWire): ScenarioUnit {
  const location = asset.location;
  const segment =
    asset.segment === 'SPACE' ? 'SPACE' : asset.segment === 'CYBER' ? 'CYBER' : 'GROUND';
  return {
    id: asset.id,
    side: 'BLU',
    ...(location.kind === 'GROUND' ? { region: location.region } : {}),
    // `ORBIT` is a band **and** a slot, so it is placed. `TRANSIT` is neither:
    // the wire gives a `from`, a `to` and a `destSlot` it has not reached, and
    // drawing it on the destination ring says it has arrived (must-not #4).
    ...(location.kind === 'ORBIT' ? { regime: location.band, slot: location.slot } : {}),
    icon: iconFor(asset.typeId, segment),
    sidc: sidcFor({
      typeId: asset.typeId,
      segment,
      // Everything in `friendly[]` is by construction the viewer's own, so the
      // affiliation is not a question the projection leaves open.
      affiliation: 'FRIEND',
      rung: null,
    }),
    name: asset.typeName,
    // The relocation, in the same words the selection rail uses for it. A moving
    // asset is still *at* its current region on the wire, and this console does
    // not draw a track it has not been given — see the phase note in
    // `maps/RegionMap.tsx`.
    desc: assetDesc(asset),
    status: asset.status,
    ...(asset.segment === 'CYBER' ? { cyber: true } : {}),
  };
}

/**
 * Where a contact goes, and the whole of the answer to must-nots #1 and #3.
 *
 * `SLOT` is the only orbital location the wire *places*. A `BAND` contact is
 * "somewhere in GEO" and the renderer has no channel for that — every position
 * it can draw is a slot position — so it is given no `regime` and appears in the
 * band strip instead (`model/orbital.ts`). `UNKNOWN` is the wire saying it
 * cannot place the contact at all, and it goes to the same strip under its own
 * heading rather than vanishing.
 *
 * Neither is stamped with a different `side` to make it draw differently:
 * `groups()` filters hostiles on `side === 'OPF'`, so an unknown stamped `UNK`
 * would be the one contact the HOSTILE TRACKS switch could not hide.
 */
function contactRegionOf(location: ContactLocationWire): Partial<ScenarioUnit> {
  switch (location.kind) {
    case 'REGION':
      return { region: location.region };
    case 'SLOT':
      return { regime: location.band, slot: location.slot };
    case 'BAND':
    case 'UNKNOWN':
      return {};
  }
}

/**
 * The marker's second line — drawn under the id while the unit is selected.
 *
 * A relocation and a transfer are the same sentence in two domains, and both are
 * in the same words the selection rail uses: the console says a thing once, in
 * one vocabulary, wherever it says it.
 */
function assetDesc(asset: FriendlyAssetViewWire): string {
  if (asset.location.kind === 'TRANSIT') {
    return '→ ' + asset.location.to + ' · T' + String(asset.location.arrivesTurn);
  }
  if (asset.relocate !== null) {
    return '→ ' + asset.relocate.toRegion + ' · T' + String(asset.relocate.completesTurn);
  }
  return asset.assignedCell;
}

function contactUnit(contact: CellViewWire['contacts'][number]): ScenarioUnit {
  const segment = contact.location.kind === 'REGION' ? 'GROUND' : 'SPACE';
  return {
    id: contact.id,
    side: 'OPF',
    ...contactRegionOf(contact.location),
    icon: iconFor(contact.presentedType, segment),
    sidc: sidcFor({
      typeId: contact.presentedType,
      segment,
      affiliation: 'HOSTILE',
      rung: contact.rung,
    }),
    name: contact.presentedTypeName,
    // A fix that expires is the one fact about a contact that changes what the
    // seat should do next, and it is the same row the selection rail draws.
    ...(contact.expiresTurn === null
      ? {}
      : { desc: 'FIX EXPIRES T' + String(contact.expiresTurn) }),
    // The corner mark is a two-state channel — flagged or not — so a contact
    // whose condition has never been assessed carries no mark. The map does not
    // claim NOMINAL anywhere in text; `contactRows` is where "AS OF T4 ·
    // UNCERTAIN" is stated, and that is a rail's job.
    status: contact.believedStatus?.status ?? 'NOMINAL',
    track: contact.rung,
  };
}

/**
 * The facade, or the region ids the pack cannot place.
 *
 * Coverage is checked here rather than left to the renderer: `buildRings`
 * `console.warn`s a missing source and carries on, which draws a theatre quietly
 * missing a quarter of itself. `theatre/TheatreView.tsx` names the ids instead.
 */
export function scenarioModel(view: CellViewWire, pack: MapPack | null): ScenarioModelResult {
  const missing = pack === null ? [] : packCoverage(view, pack);
  if (missing.length > 0) return { ok: false, missing };

  const sources = new Map((pack?.regions ?? []).map((region) => [region.id, region.sources]));
  // **No pack is a legal state, and it is the orbital board's normal one.**
  // Geometry is a client-side asset keyed by scenario (M5-D11) and the SDA board
  // needs none of it — rings, slots and debris are all on the wire. So the model
  // is built either way and `REGIONS` is simply empty, which is what the theatre
  // map's own `absent` notice is already drawn for.
  const REGIONS: readonly ScenarioRegion[] =
    pack === null
      ? []
      : view.map.regions.map((region) => ({
          id: region.id,
          // Uppercased here so the canvas and the rails agree. Scenario files carry
          // title case (`Borea`), which on this map is the only title case in the
          // product and put `BOREA` directly above `Borea` in the selection rail.
          name: region.name.toUpperCase(),
          // Non-null: `packCoverage` just proved every declared region is in the pack.
          sources: sources.get(region.id) as MapPackRegion['sources'],
        }));

  const units: ScenarioUnit[] = [
    ...view.friendly.map(assetUnit),
    ...view.contacts.map(contactUnit),
  ];
  // Ordered by the alias hash, never by arrival: `contacts[]` arrives in whatever
  // order the projection built it, and a list that reorders when one contact is
  // destroyed has told the viewer something the projection withheld
  // (`model/alias.ts`). The renderer sorts each region's own roster again, by
  // side and then id — see `maps/RegionMap.tsx` on what that does and does not
  // cost.
  const UNITS = [...units].sort((a, b) => compareAliases(a.id, b.id));
  const byId = new Map(UNITS.map((unit) => [unit.id, unit]));

  // The cyber half. Built from the same view and kept **out of `UNITS`** —
  // `hostsOf` is its own roster, and that is the structural half of must-not #1:
  // a machine revealed to cyber cannot reach a canvas that draws `UNITS`.
  const cyber = cyberModel(view);

  const model: ScenarioModel = {
    REGIONS,
    UNITS,
    WX_GLYPH,
    ROUTERS: cyber.ROUTERS,
    NETWORKS: cyber.NETWORKS,
    ACCESS_MARK,
    ACCESS_SHORT,
    ACCESS_PIPS,
    FOOTING,
    hostsOf: cyber.hostsOf,
    routerIdOf: cyber.routerIdOf,
    isCyberTeam: (unit) => unit.cyber === true,
    accessFor: (unit) => cyber.access(unit.id),
    access: cyber.access,
    hardenOf: cyber.hardenOf,
    accessRung: cyberAccessRung,
    footingFor,
    iconHref: cyberIconHref,
    networkOf: cyber.networkOf,
    unitById: (id) => byId.get(id),
    /**
     * Always true.
     *
     * The prototype's `visible` re-filtered its mock roster against
     * `window.BS_MIN_TRACK`, a dev toggle §2.1 deletes. The projection has
     * already decided what this seat may see — a contact below the seat's
     * detection floor is *absent from the view*, not present and hidden — so a
     * second client-side threshold could only hide something the server chose to
     * send. What the renderer still gets out of this seam is the one thing it was
     * built for: `slotsFor` sizes a region's slots to every unit that belongs
     * there, so nothing a layer toggle or a filter does can move a marker.
     */
    visible: () => true,
    isCyberNode: (unit) => unit.cyber === true,
    reachBands: (unit) => {
      const origin = unit.region;
      if (origin === undefined) return { cost: {}, count: 0, total: REGIONS.length };
      const cost: Record<string, number> = { [origin]: 0 };
      for (const entry of rangesFrom(view, origin)) cost[entry.region] = entry.range;
      return { cost, count: Object.keys(cost).length, total: REGIONS.length };
    },
    /**
     * `map.bandSlots`, straight through — the ring's own divisor.
     *
     * Read by `orbital-map.js` (`slotCount`) and by `model/orbital.ts`, so the
     * ring the operator counts round and the rule that prices a phase shift are
     * reading one number. A band the wire does not carry falls back to the
     * renderer's 24 rather than to zero, which would divide by nothing.
     */
    slotsIn: (band) => (view.map.bandSlots as Readonly<Record<string, number>>)[band] ?? 24,
    weatherOf: (region) => {
      const outlook = weatherFor(view, region);
      const next = outlook.next;
      // `weatherFor` says `null` for "the forecast is the value already
      // standing", which is a known next and not an unknown one.
      return [outlook.now, next === null ? outlook.now : next.known ? next.state : null];
    },
  };
  return { ok: true, model };
}
